import { AfterViewInit, Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { LogService } from '@nts/std/utility';
import { AgEditorComponent } from 'ag-grid-angular';
import { StringTextBoxComponent } from '../../../../../src/lib/components/controls/core/string-text-box/string-text-box.component';
import { StringPropertyViewModel } from '../../../../../src/lib/view-models/base-type/string-property-view-model';
import { GridComponent } from '../../grid.component';
import { ColumnCellEditorParams } from '../column_cell_editor_params.interface';
// create your cellEditor as a Angular component

const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';
const KEY_F2 = 'F2';
const KEY_ENTER = 'Enter';
const KEY_TAB = 'Tab';
const KEY_LEFT = 'ArrowLeft';
const KEY_UP = 'ArrowUp';
const KEY_RIGHT = 'ArrowRight';
const KEY_DOWN = 'ArrowDown';

@Component({
    selector: 'nts-string-text-box-editor-cell',
    styleUrls: ['./string-cell-editor.component.scss'],
    imports: [
        StringTextBoxComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <nts-string-text-box
            #textBox
            (keyDown)="onKeyDown($event)"
            (onInputChange)="onInputChange($event)"
            (onFinishEditing)="onFinishEditing()"
            [listenClickOutside]="true"
            [updateOnBlur]="false"
            [updateOnFocusOut]="false"
            [showErrorBorder]="false"
            [propertyViewModel]="propertyViewModel"
            [initialValue]="initialValue"
            [selectAllOnFocus]="false"
            [style.width.px]="cellWidth"
            [style.height.px]="cellHeight"
            [modelUpdate]="false">
        </nts-string-text-box>
    `
})
export class StringCellEditorComponent implements AfterViewInit, OnInit, AgEditorComponent {

  @Input()
  params: ColumnCellEditorParams;

  @ViewChild('textBox', { static: true })
  textBox: StringTextBoxComponent;

  returnedValue: string | null;
  highlightAllOnFocus = true;
  initialValue: string | null;

  private cancelBeforeStart = false;
  private internalPropertyViewModel: StringPropertyViewModel;

  get propertyViewModel(): StringPropertyViewModel {
    if (!this.internalPropertyViewModel) {
      this.internalPropertyViewModel = this.params.columnInfo.fieldName.split('.').reduce(
        (o, i) => {
          if (i === 'value') {
            return o;
          }
          return o[i];
        }, this.params.data) as StringPropertyViewModel;
    }
    return this.internalPropertyViewModel;
  }

  get cellWidth() {
    return this.params.eGridCell.clientWidth;
  }

  get cellHeight() {
    return this.params.eGridCell.clientHeight;
  }

  get gridContext(): GridComponent {
    return this.params.context;
  }

  ngOnInit(): void {
    if (this.params) {
      this.agInit(this.params);
    }
  }

  agInit(params: ColumnCellEditorParams): void {
    this.params = params;
    this.setInitialValue(this.params);

    this.cancelBeforeStart = false;
  }

  // dont use afterGuiAttached for post gui events - hook into ngAfterViewInit instead for this
  ngAfterViewInit() {

    window.setTimeout(() => {
      const input = this.textBox?.input;
      if (!input) {
        return;
      }

      this.textBox.focus();
      if (this.highlightAllOnFocus) {
        input.select();

        this.highlightAllOnFocus = false;
      } else {
        // when we started editing, we want the carot at the end, not the start.
        // this comes into play in two scenarios: a) when user hits F2 and b)
        // when user hits a printable character, then on IE (and only IE) the carot
        // was placed after the first character, thus 'apply' would end up as 'pplea'
        const length = input.value
          ? input.value.length
          : 0;
        if (length > 0) {
          input.setSelectionRange(length, length);
        }
      }

      this.textBox.focus();
    });
  }

  getValue(): string {
    return this.returnedValue;
  }

  setInitialValue(params: ColumnCellEditorParams): void {

    this.initialValue = null;
    let highlightAllOnFocus = true;

    if (params.eventKey === KEY_BACKSPACE || params.eventKey === KEY_DELETE) {
      // Se spingo can o backspace

      // Passo stringa vuota come start value
      this.initialValue = '';

      // Non evidenzio la cella
      highlightAllOnFocus = true;

      this.returnedValue = this.initialValue;

    } else if (params.eventKey === KEY_ENTER || params.eventKey === KEY_F2) {
      // Se spingo invio o F2

      // Recupero il valore del pvm
      this.initialValue = null;

      // Evidenzio la cella
      highlightAllOnFocus = true;

      this.returnedValue = params.columnInfo.fieldName.split('.').reduce(
        (o, i) => {
          if (i === 'value') {
            return o[i];
          }
          return o[i];
        }, this.params.data
      );


    } else if (params.eventKey) {

      // Se passo un numero, inizio con quello
      this.initialValue = params.eventKey;

      // Non evidenzio la cella
      highlightAllOnFocus = false;

      this.returnedValue = this.initialValue;
    } else {

      // Negli altri casi visualizzo quello che c'è gia nella cella
      this.initialValue = null;
      this.returnedValue = params.columnInfo.fieldName.split('.').reduce(
        (o, i) => {
          if (i === 'value') {
            return o[i];
          }
          return o[i];
        }, this.params.data
      );
    }
    this.highlightAllOnFocus = highlightAllOnFocus;
  }

  onInputChange(e: any) {
    if (!e?.target) {
      return;
    }
    this.returnedValue = e.target.value;
  }

  blur($event) {
    this.propertyViewModel.validate();
  }

  onFinishEditing() {
    this.params.stopEditing();
  }

  isCancelBeforeStart(): boolean {
    return this.cancelBeforeStart;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.isLeftOrRight(event) || this.deleteOrBackspace(event)) {
      event.stopPropagation();
      return;
    }

    if (
      !this.finishedEditingPressed(event) &&
      !this.isKeyPressedString(event)
    ) {
      if (event.preventDefault) { event.preventDefault(); }
    }

    if (this.isUpOrDown(event)) {
      this.navigateToNextCell(event);
    }
  }

  // for testing
  setValue(newValue: any) {
    if (!this.textBox?.input) {
      return;
    }

    this.textBox.input.value = newValue ?? '';
    this.returnedValue = this.textBox.input.value;
  }

  private isKeyPressedString(event: KeyboardEvent): boolean {
    return true;
  }

  private deleteOrBackspace(event: any) {
    return (
      [KEY_DELETE, KEY_BACKSPACE].indexOf(event.key) > -1
    );
  }

  private isLeftOrRight(event: KeyboardEvent) {
    return [KEY_LEFT, KEY_RIGHT].indexOf(event.key) > -1;
  }

  private isUpOrDown(event: KeyboardEvent) {
    return [KEY_UP, KEY_DOWN].indexOf(event.key) > -1;
  }

  private finishedEditingPressed(event: KeyboardEvent) {
    return event.key === KEY_ENTER || event.key === KEY_TAB;
  }

  private navigateToNextCell(event: KeyboardEvent) {

    const fc = this.gridContext.api.getFocusedCell();
    LogService.debug('focused cell rowindex ' + fc.rowIndex, 'focused celll column ' + fc.column);
    if (event.key === KEY_DOWN) {
      // set selected cell on current cell + 1
      this.gridContext.api.forEachNode((node) => {
        if (fc.rowIndex + 1 === node.rowIndex) {
          this.gridContext.api.setFocusedCell(node.rowIndex, fc.column);
          this.gridContext.api.startEditingCell({
            rowIndex: node.rowIndex,
            colKey: fc.column
          });
        }
      });
    } else if (event.key === KEY_UP) {
      // set selected cell on current cell + 1
      this.gridContext.api.forEachNode((node) => {
        if (fc.rowIndex - 1 === node.rowIndex) {
          this.gridContext.api.setFocusedCell(node.rowIndex, fc.column);
          this.gridContext.api.startEditingCell({
            rowIndex: node.rowIndex,
            colKey: fc.column
          });
        }
      });
    } else if (event.key === KEY_LEFT) {
      this.gridContext.api.tabToPreviousCell();
    } else if (event.key === KEY_RIGHT) {
      this.gridContext.api.tabToNextCell();
    }
  }

}
